/**
 * ═══ CE QUE L'API DIT DU DIRECT ═══
 *
 * Le miroir exact de `api/src/helpers/live.js` et `livePayload.js`. Les champs
 * optionnels le sont parce que le SERVEUR les retire selon le rôle, pas parce
 * qu'ils manqueraient parfois : `isRightAnswer` est absent tant que les résultats
 * ne sont pas montrés, et c'est le seul endroit où la bonne réponse existe.
 *
 * ⚠️ NE JAMAIS RENDRE CES CHAMPS OBLIGATOIRES pour se simplifier la vie. Un `!`
 * de complaisance ici, et l'écran promet une donnée que le serveur a délibérément
 * retenue : il affichera `undefined` au participant, ou pire, quelqu'un ajoutera
 * une route qui la renvoie « pour que ça marche ».
 */

import type { Reveal } from "./reveal";

/** Les six types, tels que l'API les nomme. */
export type ActivityType =
  | "poll"
  | "quiz"
  | "ballot"
  | "questions"
  | "cloud"
  /**
   * ⚠️ CELUI-LÀ NE SE RÉPOND PAS, IL SE REGARDE. Le classement est devenu une
   * activité pour retrouver un déclencheur : il était commandé par un sélecteur
   * « ce que la salle affiche » retiré au tri du 2026-08-12, et sa route restait
   * écrite et testée sans que rien puisse l'afficher. Sa phase ne gouverne donc rien
   * d'autre que sa présence à l'écran. Le serveur refuse toute réponse (`WATCHED_TYPES`).
   */
  | "leaderboard";

/** Ce que la phase autorise : `open` seule accepte une réponse. */
export type Phase = "draft" | "open" | "closed";

/** Qui regarde. Décidé par le serveur, jamais par le client. */
export type Role = "participant" | "screen" | "control";

export type Choice = {
  id: number;
  label: string;
  position: number;
  image: string | null;
  color: string | null;
  /** Présents seulement quand les résultats sont montrés. Voir l'avertissement du fichier. */
  points?: number;
  isRightAnswer?: boolean;
};

export type Activity = {
  id: number;
  type: ActivityType;
  title: string;
  content: string | null;
  phase: Phase;
  showResults: boolean;
  config: {
    maxAnswers?: number;
    display?: "bars" | "gauge" | "pie";
    revealRightAnswer?: boolean;
    moderation?: boolean;
    colors?: string[];
    font?: string | null;
    maxWords?: number;
    /**
     * L'animation qui accompagne « Montrer les résultats », et ses options. Lue par
     * `readReveal`, jamais telle quelle : c'est un blob libre côté serveur.
     */
    reveal?: Reveal;
  };
  choices: Choice[];
  /** Régie seulement. */
  position?: number;
  openedAt?: string | null;
  closedAt?: string | null;
  /**
   * QUAND ON EN A FINI AVEC ELLE, ou `null`.
   *
   * ⚠️ UN FAIT, PAS UNE DÉDUCTION. La régie rangeait sa liste en déduisant « close et
   * plus à l'écran », ce qui étiquetait « terminée » toute activité simplement
   * remplacée par la suivante : on la clôt pour commenter ses résultats, on en lance
   * une autre, et elle disparaissait du groupe où on la cherchait encore. Seul le
   * geste « Terminer » pose cette date, et remettre l'activité en jeu l'efface.
   */
  endedAt?: string | null;
};

export type Results = {
  perChoice: { choiceId: number; votes?: number; voters?: number; share?: number }[];
  totalVotes?: number;
  totalVoters?: number | null;
  /** Les chiffres sont masqués : il ne reste que les parts. */
  hidden: boolean;
};

export type TextAnswer = {
  id: number;
  text: string;
  createdAt: string;
  /** Régie seulement : l'état de modération. */
  state?: "received" | "kept" | "discarded";
};

export type LiveState = {
  broadcast: "before" | "live" | "ended";
  scene: "activity" | "leaderboard" | "reactions" | "nothing";
  activityId: number | null;
  showCounts: boolean;
  hideVoteCounts: boolean;
  /**
   * La réception des SMS est ouverte sur cet événement : l'écran de salle donne la
   * consigne du vote par SMS (voir `smsVote.ts`). Jamais les SMS eux-mêmes, qui
   * restent à la régie.
   */
  sms: boolean;
};

export type LivePayload = {
  role: Role;
  state: LiveState;
  activity: Activity | null;
  results?: Results | null;
  cloud?: { text: string; count: number }[];
  answers?: TextAnswer[];
  spotlight?: { id: number; text: string } | null;
  /** Seulement sur la lecture, jamais sur la diffusion. Voir `livePayload.js`. */
  myAnswers?: { id: number; choiceId: number | null; text: string | null }[];
  /**
   * Combien de téléphones sont connectés au direct.
   *
   * ⚠️ `null` VEUT DIRE « ON NE SAIT PAS », pas « personne ». Sans canal temps réel
   * configuré, aucune socket n'est ouverte et le serveur préfère se taire plutôt
   * qu'annoncer zéro spectateur devant une salle pleine.
   */
  viewers?: number | null;
  /**
   * La séquence de réactions ouverte, s'il y en a une.
   *
   * ⚠️ AUSSI DANS LA CHARGE, PAS SEULEMENT DANS LE CANAL. Le websocket l'annonce
   * (`NEW_SEQUENCE`), mais il n'y a pas toujours de websocket : sans elle ici, le
   * bloc de réactions n'apparaissait NULLE PART en production.
   */
  sequenceId?: number | null;
  /**
   * Combien de réponses porte l'activité à l'écran, en direct.
   *
   * ⚠️ DES LIGNES, comme `answerCount` de la liste de la régie, et pas « combien de
   * personnes » : deux nombres qui se ressemblent feraient sauter le chiffre à chaque
   * relecture de la liste.
   */
  answerCount?: number;
};

/**
 * LE NOM DE MODULE DU PROTOTYPE, à partir du type de l'API.
 *
 * ⚠️ LES DEUX VOCABULAIRES SONT VOLONTAIREMENT DIFFÉRENTS. Le code s'écrit en
 * anglais ; les valeurs de `data-mod` sont celles de la maquette, en français, et
 * la trappe CSS vendue au client vise `[data-mod="sondage"]`. La traduction se
 * fait ici, à un seul endroit.
 */
export const MOD_ATTR: Record<ActivityType, string> = {
  poll: "sondage",
  quiz: "quiz",
  ballot: "vote",
  questions: "questions",
  cloud: "nuage",
  /* La maquette dessine `.podium` et `.ld` : le mot de la trappe CSS est celui-là. */
  leaderboard: "classement",
};

/**
 * LES SIX TYPES, DANS L'ORDRE OÙ LE PRODUIT LES PRÉSENTE.
 *
 * ⚠️ DÉCLARÉ, PAS DÉDUIT DES CLÉS DE `MOD_ATTR`. Un sélecteur bâti sur l'ordre des
 * clés d'un objet raconte l'histoire de la table plutôt que celle du produit, et il
 * change le jour où quelqu'un réordonne la table pour la lire mieux. La liste est
 * tenue par un test contre `MOD_ATTR` : un septième type qu'on oublierait ici
 * disparaîtrait du filtre sans que rien ne le dise.
 */
export const ACTIVITY_TYPES: ActivityType[] = [
  "poll",
  "quiz",
  "ballot",
  "questions",
  "cloud",
  "leaderboard",
];

/** Les types où le public choisit dans une liste. */
export const CHOICE_TYPES: ActivityType[] = ["poll", "quiz", "ballot"];
/** Les types où le public écrit. */
export const TEXT_TYPES: ActivityType[] = ["questions", "cloud"];
/** Les types que le public ne fait que regarder. */
export const WATCHED_TYPES: ActivityType[] = ["leaderboard"];
/**
 * Les types qui se votent aussi par SMS, réception ouverte. Pas le vote (`ballot`) :
 * réservé aux ayants droit, et un numéro n'est personne. Miroir de `SMS_VOTE_TYPES`
 * dans `api/src/helpers/live.js`.
 */
export const SMS_VOTE_TYPES: ActivityType[] = ["poll", "quiz"];
